import { getMembershipShareCanonicalStepId } from "./membershipShareRoutePolicy";
import { autoLoanFlow } from "../../navigation/flows/autoLoanFlow";
import type { paths } from "../../types/schema-lending";
import type { FlowType } from "@lending/navigation/types";
import type { MembershipShareAccessResponse } from "@lending/types";

type AutoLoanApplication =
  paths["/v2/lending/vehicle_loan_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"];

interface AutoLoanRoutePolicyResult {
  canonicalStepId: string | null;
}

const AUTO_LOAN_WEB_FLOW_STEP_ORDER = autoLoanFlow.web.map((step) => step.id);

function isStepAheadOf(currentStepId: string, maxAllowedStepId: string): boolean {
  const currentIndex = AUTO_LOAN_WEB_FLOW_STEP_ORDER.indexOf(currentStepId);
  const maxAllowedIndex = AUTO_LOAN_WEB_FLOW_STEP_ORDER.indexOf(maxAllowedStepId);

  if (currentIndex === -1 || maxAllowedIndex === -1) {
    return false;
  }

  return currentIndex > maxAllowedIndex;
}

function getAutoLoanCanonicalRoutePolicy(
  application: AutoLoanApplication,
  currentStepId: string | null,
  membershipShareAccess?: MembershipShareAccessResponse,
  flowType: FlowType = "web",
  shouldCollectAutoRequestedAmount = false
): AutoLoanRoutePolicyResult {
  const membershipShareCanonicalStepId = getMembershipShareCanonicalStepId(
    application.state,
    currentStepId,
    application.account_application_uuid,
    membershipShareAccess,
    flowType
  );

  if (membershipShareCanonicalStepId !== undefined) {
    return { canonicalStepId: membershipShareCanonicalStepId };
  }

  if (!currentStepId) {
    switch (application.state) {
      case "creating":
        return { canonicalStepId: "personal-info" };
      case "decisioning_queued":
      case "decisioning_processing":
        return {
          canonicalStepId: shouldCollectAutoRequestedAmount ? "requested-amount" : "credit-pull",
        };
      case "offers_generated":
      case "offer_accepted":
        return { canonicalStepId: "approved" };
      default:
        return { canonicalStepId: null };
    }
  }

  switch (application.state) {
    case "creating":
    case "decisioning_queued":
    case "decisioning_processing": {
      const maxAllowedStepId = shouldCollectAutoRequestedAmount
        ? "requested-amount"
        : "credit-pull";

      return {
        canonicalStepId: isStepAheadOf(currentStepId, maxAllowedStepId) ? maxAllowedStepId : null,
      };
    }
    case "offers_generated":
      return { canonicalStepId: isStepAheadOf(currentStepId, "approved") ? "approved" : null };
    case "offer_accepted":
      return { canonicalStepId: "approved" };
    default:
      return { canonicalStepId: "submitted" };
  }
}

export { getAutoLoanCanonicalRoutePolicy };
